import React from 'react';
import { DashboardControl } from 'devexpress-dashboard';
import { localStorageService } from '../../services/localstorage-service';
import { UserRoleType } from '../../utils/user-role-type';
import { apiconst } from '../../utils/app-const';
import { DashboardPanelExtension } from 'devexpress-dashboard/common';
import { authenticationService } from '../../services/AuthService'
import { webDashboardService } from '../../services/WebDashboardService'
import { applyDashboardLocalization } from '../../localization/dashboard-localization'

const emptyConditionalFormattingModel = {
    RuleModels: [],
    FormatConditionStyleSettings: []
};

function patchDashboardItemResponse(data) {
    if (data && data.ConditionalFormattingModel === null) {
        data.ConditionalFormattingModel = emptyConditionalFormattingModel;
    }
    return data;
}

function isDashboardItemDataUrl(url) {
    return typeof url === 'string' && url.includes('DashboardItemGetAction');
}

function installDashboardAjaxCompatibility() {
    if (window.__dashboardAjaxCompatInstalled) {
        return;
    }
    window.__dashboardAjaxCompatInstalled = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
        const response = await originalFetch(...args);
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
        if (!isDashboardItemDataUrl(url)) {
            return response;
        }
        try {
            const data = await response.clone().json();
            if (data?.ConditionalFormattingModel === null) {
                patchDashboardItemResponse(data);
                return new Response(JSON.stringify(data), {
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers
                });
            }
        } catch {
            // Keep original response when body is not JSON.
        }
        return response;
    };

    const xhrProto = XMLHttpRequest.prototype;
    const responseTextDescriptor = Object.getOwnPropertyDescriptor(xhrProto, 'responseText');
    const responseDescriptor = Object.getOwnPropertyDescriptor(xhrProto, 'response');
    const originalOpen = xhrProto.open;

    const patchXhrJsonResponse = (xhr, rawText) => {
        if (!rawText || xhr.readyState !== 4 || !isDashboardItemDataUrl(xhr._dashboardItemDataUrl)) {
            return rawText;
        }
        try {
            const data = JSON.parse(rawText);
            if (data?.ConditionalFormattingModel === null) {
                return JSON.stringify(patchDashboardItemResponse(data));
            }
        } catch {
            // Ignore non-JSON responses.
        }
        return rawText;
    };

    xhrProto.open = function (method, url, ...rest) {
        this._dashboardItemDataUrl = url;
        return originalOpen.call(this, method, url, ...rest);
    };

    if (responseTextDescriptor?.get) {
        const originalResponseTextGet = responseTextDescriptor.get;
        Object.defineProperty(xhrProto, 'responseText', {
            get: function () {
                return patchXhrJsonResponse(this, originalResponseTextGet.call(this));
            },
            configurable: true
        });
    }

    if (responseDescriptor?.get) {
        const originalResponseGet = responseDescriptor.get;
        Object.defineProperty(xhrProto, 'response', {
            get: function () {
                const raw = originalResponseGet.call(this);
                return typeof raw === 'string' ? patchXhrJsonResponse(this, raw) : raw;
            },
            configurable: true
        });
    }
}

const SUPPLIER_ACCESS_VIEW_ONLY = 'ViewOnly';
const SUPPLIER_ACCESS_PRIVILEGED = 'Privileged';
const REPLENISHMENT_DASHBOARD_ID = '1100';

function getInitialDashboardId() {
    const supplierAccess = localStorageService.getSupplierAccess();
    if (supplierAccess?.defaultDashboardId != null) {
        return String(supplierAccess.defaultDashboardId);
    }
    return '0';
}

function getSupplierAccessMessage() {
    const supplierAccess = localStorageService.getSupplierAccess();
    if (supplierAccess?.mode === SUPPLIER_ACCESS_VIEW_ONLY && (!supplierAccess.supplierId || supplierAccess.supplierId === 0)) {
        return 'No supplier is linked to your user. Contact your store administrator.';
    }
    return null;
}

class Dashboard extends React.Component {
    constructor(props) {
        super(props);
        this.dashboardRef = React.createRef();
        this.state = {
            permission: 'Viewer',
            dashboardId: getInitialDashboardId(),
            supplierAccessMessage: getSupplierAccessMessage()
        }
    }
    dashboardChanged = async (e) => {
        let workingMode;
        if (e.name === "dashboardId") {
            if (this.dashboardControl
                && this._lastDashboardId !== undefined
                && this._lastDashboardId !== e.value) {
                // Avoid stale item state from another dashboard (e.g. cardDashboardItem1 on Supplier).
                this.dashboardControl.setDashboardState('{}');
            }
            this._lastDashboardId = e.value;
            if (e.value === "0" && this.dashboardControl) {
                this.dashboardControl.switchToViewer();
                this.applyStandardDashboardInitialState();
            }
            const replenishmentId = String(
                localStorageService.getSupplierAccess()?.replenishmentDashboardId ?? REPLENISHMENT_DASHBOARD_ID
            );
            if (e.value === replenishmentId && this.dashboardControl) {
                this.applySupplierReplenishmentInitialState();
            }
            if (e.value > "0") {
                workingMode = await webDashboardService.GetWorkingMode(e.value);
                if (workingMode === 'Viewer') {
                    this.dashboardControl.switchToViewer();
                }
                if (workingMode === 'Designer') {
                    this.dashboardControl.switchToDesigner();
                }
            }
        }
        this.setState({
            permission: workingMode ? workingMode : 'Viewer',
            dashboardId: e.value
        });
    };

    applyStandardDashboardInitialState = () => {
        if (!this.dashboardControl) {
            return;
        }
        const currentDate = new Date();
        this.dashboardControl.setDashboardState(JSON.stringify({
            Items: {
                comboBoxDashboardItem1: {
                    MasterFilterValues: [[currentDate.getMonth() + 1]]
                }
            }
        }));
    };

    applySupplierReplenishmentInitialState = () => {
        if (!this.dashboardControl) {
            return;
        }
        const supplierAccess = localStorageService.getSupplierAccess();
        if (!supplierAccess?.supplierName || !supplierAccess.supplierId) {
            return;
        }
        // View-only: combo is removed server-side; filter is enforced in API.
        if (supplierAccess.mode === SUPPLIER_ACCESS_VIEW_ONLY) {
            return;
        }
        // Privileged: pre-select linked supplier; combo still has ShowAllValue ("Alle") to view all suppliers.
        const stateJson = JSON.stringify({
            Items: {
                comboBoxSupplier: {
                    MasterFilterValues: [[supplierAccess.supplierName]]
                }
            }
        });
        const applyState = () => {
            if (this.dashboardControl) {
                this.dashboardControl.setDashboardState(stateJson);
            }
        };
        applyState();
        window.setTimeout(applyState, 300);
    };

    onDashboardInitialized = (e) => {
        if (e.dashboardId === '0') {
            this.applyStandardDashboardInitialState();
            return;
        }
        const replenishmentId = String(
            localStorageService.getSupplierAccess()?.replenishmentDashboardId ?? REPLENISHMENT_DASHBOARD_ID
        );
        if (e.dashboardId === replenishmentId) {
            this.applySupplierReplenishmentInitialState();
        }
    };

    onBeforeDashboardRender = (a) => {
    };

    loadDashboards = () => {
        applyDashboardLocalization();
        installDashboardAjaxCompatibility();

        const userRole = localStorageService.getUserRoleType();
        const permission = this.verifyWorkingMode(userRole?.split(',')?.map((r) => r.trim()));
        this.editingAllowed = permission === "Designer";
        this.setState({ permission });

        this.dashboardControl = new DashboardControl(this.dashboardRef.current, {
            // Specifies the URL where the Web Dashboard's server side is hosted.
            endpoint: apiconst.ENDPOINT + apiconst.DASHBOARD,
            workingMode: this.state.permission,
            dashboardId: this.state.dashboardId,
            // DevExpress default true suppresses the "Show all" option in filter combos; false is required for supplier "Alle".
            useNeutralFilterMode: false,
            // SQL String editor in the Data Source Wizard: requires API AllowExecutingCustomSql = true (RetailLinkDashboardAPI / Portal).
            extensions: {
                'data-source-wizard': {
                    enableCustomSql: true
                }
            },
            // workingMode: "ViewerOnly",
            MobileLayoutMode: 'Always',
            onBeforeRender: this.onBeforeDashboardRender,
            onOptionChanged: this.dashboardChanged,
            onDashboardInitialized: this.onDashboardInitialized,
            ajaxRemoteService: {
                headers: {
                    'authorization': "bearer " + localStorageService.getAuthToken()
                },
                complete: (jqXHR) => {
                    if (!jqXHR?.responseJSON || jqXHR.responseJSON.ConditionalFormattingModel !== null) {
                        return;
                    }
                    patchDashboardItemResponse(jqXHR.responseJSON);
                }
            }
        });
        return this.dashboardControl;
    };

    verifyWorkingMode = (userRoles) => {
        if (!userRoles || !userRoles.length) {
            return "Viewer";
        }
        const editRights = [
            UserRoleType.EDITSTORE,
            UserRoleType.EDITCHAIN,
            UserRoleType.EDITCLUSTER
        ];
        const hasEdit = userRoles.some((r) => editRights.includes(r));
        if (hasEdit) {
            return "Designer";
        }
        const viewRights = [
            UserRoleType.VIEWSTORE,
            UserRoleType.VIEWCHAIN,
            UserRoleType.VIEWCLUSTER
        ];
        const hasView = userRoles.some((r) => viewRights.includes(r));
        return hasView ? "Viewer" : "Viewer";
    }

    checkAuthentication = async () => {
        let isAuthented = await authenticationService.CheckAuthentication();
        if (!isAuthented) {
            localStorageService.clearStorage();
            window.location.reload(true);
        }
        else {
            if (this.dashboardControl === undefined) {
                const dashboardControl = this.loadDashboards();
                // The dashboardControl variable is the obtained DashboardControl instance.
                // var panelExtension = new DashboardPanelExtension(dashboardControl, { dashboardThumbnail: '/DashboardThumbnail/{0}.png' });
                var panelExtension = new DashboardPanelExtension(dashboardControl);
                dashboardControl.registerExtension(panelExtension);
                // Allow toolbar switch to Designer when user has Edit.* rights (matches API GetWorkingMode intent).
                panelExtension.allowSwitchToDesigner(this.editingAllowed);
                dashboardControl.render();
            }
        }
    }

    componentDidMount() {
        var a2hsBannerDiv = document.querySelector('.block__install');
        if (a2hsBannerDiv)
            a2hsBannerDiv.style.display = 'none';
        this.checkAuthentication();
    }

    componentWillUnmount() {
        if (this.dashboardControl)
            this.dashboardControl.dispose();
    }
    onEndUpdate(args) {
        console.log(args);
    }

    render() {
        const { supplierAccessMessage } = this.state;
        return (
            <div>
                {supplierAccessMessage && (
                    <div
                        className="supplier-access-message"
                        style={{
                            padding: '12px 16px',
                            margin: '8px',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '4px',
                            color: '#664d03'
                        }}
                        role="alert"
                    >
                        {supplierAccessMessage}
                    </div>
                )}
                <div id="scrollviewdash">
                    <div ref={this.dashboardRef} style={{ width: '100%', height: '100%' }}></div>
                </div>
            </div>
        );
    }
}

export default Dashboard;